/**
 * La pantalla de evaluación (`GET resources/evaluation`), la primera que se pide después del cierre.
 *
 * A la evaluación se llega con la inspección marcada terminada (`InspectionFinishedCheck`), así que un
 * fallo de esta consulta es de la pantalla, no del cierre. Por eso no pasa por la pantalla de error, que
 * presenta un fallo y no tiene salida: el asegurado ve que su inspección quedó enviada y puede reintentar.
 * Vale para cualquier fallo, no solo sin red.
 */
import type { IAppEvaluation } from '../interfaces/Outputs/AppEvaluation';
import { CLOSE_CONFIRMED_TEXT } from './FinalizeClose';

export const EVALUATION_ENDPOINT = 'resources/evaluation';

/**
 * Sin plazo propio, una red que no responde ni falla deja al asegurado bajo el spinner todo lo que tarde
 * el navegador en cortar. Al vencer, ve el estado de «enviada» y puede reintentar.
 */
export const EVALUATION_SCREEN_TIMEOUT_MS = 15_000;

export type EvaluationFetchOutcome =
  | { kind: 'ok'; screen: IAppEvaluation }
  | { kind: 'unavailable'; detail: string };

/** `httpStatus` es `null` cuando no hubo respuesta: red caída, timeout o un fallo armando las cabeceras. */
export function classifyEvaluationResponse(httpStatus: number | null, body: unknown): EvaluationFetchOutcome {
  if (httpStatus === null) {
    return { kind: 'unavailable', detail: 'sin respuesta' };
  }
  if (httpStatus < 200 || httpStatus >= 300) {
    return { kind: 'unavailable', detail: `HTTP ${httpStatus}` };
  }
  const screen = (body as { EvaluationScreen?: unknown } | null)?.EvaluationScreen;
  if (!screen || typeof screen !== 'object') {
    return { kind: 'unavailable', detail: 'respuesta sin EvaluationScreen' };
  }
  return { kind: 'ok', screen: screen as IAppEvaluation };
}

/** El mismo título del cierre confirmado: para el asegurado es el mismo estado, visto desde otra pantalla. */
export const EVALUATION_UNAVAILABLE_TEXT = {
  title: CLOSE_CONFIRMED_TEXT.title,
  message: 'No pudimos cargar esta pantalla. Revisa tu conexión o vuelve a intentarlo en unos momentos.',
  retry: 'Reintentar',
} as const;
